/**
 * @bahar/fsrs - Shared FSRS utilities for flashcard spaced repetition
 *
 * Provides utilities for the FSRS (Free Spaced Repetition Scheduler) algorithm
 * used across web and mobile apps.
 */

import {
  type FlashcardDirection,
  FlashcardState,
  type SelectFlashcard,
} from "@bahar/drizzle-user-db-schemas";

import {
  type Card,
  createEmptyCard,
  type FSRS,
  type FSRSParameters,
  fsrs,
  type Grade,
  generatorParameters,
  type RecordLog,
  type ReviewLog,
  type State,
} from "ts-fsrs";

/**
 * Converts a database flashcard to an FSRS Card with Date objects
 */
export const toFsrsCard = (
  flashcard: SelectFlashcard
): Card & { id: string } => {
  return {
    id: flashcard.id,
    due: new Date(flashcard.due),
    stability: flashcard.stability ?? 0,
    difficulty: flashcard.difficulty ?? 0,
    elapsed_days: flashcard.elapsed_days ?? 0,
    scheduled_days: flashcard.scheduled_days ?? 0,
    reps: flashcard.reps ?? 0,
    lapses: flashcard.lapses ?? 0,
    state: (flashcard.state ?? FlashcardState.NEW) as State,
    learning_steps: flashcard.learning_steps ?? 0,
    last_review: flashcard.last_review
      ? new Date(flashcard.last_review)
      : undefined,
  };
};

/**
 * Converts an FSRS Card back to database format
 */
export const fromFsrsCard = (
  card: Card,
  dictionaryEntryId: string,
  direction: FlashcardDirection
): Omit<SelectFlashcard, "id" | "is_hidden"> => {
  const dueTimestampMs = card.due.getTime();
  const lastReviewTimestampMs = card.last_review?.getTime() ?? null;

  return {
    dictionary_entry_id: dictionaryEntryId,
    difficulty: card.difficulty,
    due: card.due.toISOString(),
    due_timestamp_ms: dueTimestampMs,
    elapsed_days: card.elapsed_days,
    lapses: card.lapses,
    last_review: card.last_review?.toISOString() ?? null,
    last_review_timestamp_ms: lastReviewTimestampMs,
    learning_steps: card.learning_steps,
    reps: card.reps,
    scheduled_days: card.scheduled_days,
    stability: card.stability,
    state: card.state,
    direction,
  };
};

/**
 * Creates a new empty flashcard for a dictionary entry
 */
export const createNewFlashcard = (
  dictionaryEntryId: string,
  direction: FlashcardDirection
): Omit<SelectFlashcard, "id" | "is_hidden"> => {
  const emptyCard = createEmptyCard();
  return fromFsrsCard(emptyCard, dictionaryEntryId, direction);
};

/**
 * Creates an FSRS scheduler instance with fuzzing enabled
 */
export const createScheduler = (opts?: Partial<FSRSParameters>): FSRS => {
  const params = generatorParameters({ enable_fuzz: true, ...opts });

  return fsrs(params);
};

/**
 * Gets the scheduling options for a card at the current time
 */
export const getSchedulingOptions = (
  scheduler: FSRS,
  flashcard: SelectFlashcard,
  now: Date = new Date()
): RecordLog => {
  const fsrsCard = toFsrsCard(flashcard);
  return scheduler.repeat(fsrsCard, now);
};

/**
 * Grades a flashcard and returns the updated database fields alongside the
 * FSRS review log for the review, so a caller can persist both the card and
 * its revlog entry from a single scheduling run. Mirrors
 * {@link forgetFlashcard}'s shape.
 */
export const gradeFlashcard = (
  scheduler: FSRS,
  flashcard: SelectFlashcard,
  grade: Grade,
  now: Date = new Date()
): {
  card: Pick<
    SelectFlashcard,
    | "due"
    | "due_timestamp_ms"
    | "last_review"
    | "last_review_timestamp_ms"
    | "state"
    | "stability"
    | "difficulty"
    | "reps"
    | "lapses"
    | "elapsed_days"
    | "scheduled_days"
    | "learning_steps"
  >;
  log: ReviewLog;
} => {
  const fsrsCard = toFsrsCard(flashcard);
  const { card, log } = scheduler.repeat(fsrsCard, now)[grade];

  return {
    card: {
      due: card.due.toISOString(),
      due_timestamp_ms: card.due.getTime(),
      last_review: card.last_review?.toISOString() ?? null,
      last_review_timestamp_ms: card.last_review?.getTime() ?? null,
      state: card.state,
      stability: card.stability,
      difficulty: card.difficulty,
      reps: card.reps,
      lapses: card.lapses,
      elapsed_days: card.elapsed_days,
      scheduled_days: card.scheduled_days,
      learning_steps: card.learning_steps,
    },
    log,
  };
};

/**
 * Resets a flashcard back to a new card and returns the updated database
 * fields alongside the FSRS review log for the reset.
 *
 * The log has `rating === Rating.Manual` and records the card's pre-reset
 * state, so it can be persisted as a `manual` revlog marking the reset in the
 * card's review history.
 */
export const forgetFlashcard = (
  scheduler: FSRS,
  flashcard: SelectFlashcard,
  now: Date = new Date()
): {
  card: Pick<
    SelectFlashcard,
    | "due"
    | "due_timestamp_ms"
    | "last_review"
    | "last_review_timestamp_ms"
    | "state"
    | "stability"
    | "difficulty"
    | "reps"
    | "lapses"
    | "elapsed_days"
    | "scheduled_days"
    | "learning_steps"
  >;
  log: ReviewLog;
} => {
  const fsrsCard = toFsrsCard(flashcard);
  // reset_count: true zeroes the lifetime reps/lapses counters (forget keeps
  // them by default). forget always preserves last_review, so we null it here
  // to match a brand-new card.
  const { card, log } = scheduler.forget(fsrsCard, now, true);

  return {
    card: {
      due: card.due.toISOString(),
      due_timestamp_ms: card.due.getTime(),
      last_review: null,
      last_review_timestamp_ms: null,
      state: card.state,
      stability: card.stability,
      difficulty: card.difficulty,
      reps: card.reps,
      lapses: card.lapses,
      elapsed_days: card.elapsed_days,
      scheduled_days: card.scheduled_days,
      learning_steps: card.learning_steps,
    },
    log,
  };
};
